import React, { MouseEvent, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'




import Backdrop from '@components/utils/Backdrop/Backdrop'
import useBodyScroll from '@lib/hooks/useBodyScroll'
import { KeyCode } from '@lib/hooks/useKeyboard/codes'
import useKeyboard from '@lib/hooks/useKeyboard/useKeyboard'
import usePortal from '@lib/hooks/usePortal'
import clsx from 'clsx'
import DrawerCloseIcon from '../Icons/DrawerCloseIcon'
import DrawerWrapper from './drawer-wrapper'
import s from "./drawer.module.scss"
import { DrawerPlacement } from './helpers'

interface Props {
    visible?: boolean
    keyboard?: boolean
    disableBackdropClick?: boolean
    onClose?: () => void
    onContentClick?: (event: MouseEvent<HTMLElement>) => void
    wrapClassName?: string
    placement?: DrawerPlacement
    closeIcon?: boolean
}

const defaultProps = {
    wrapClassName: '',
    keyboard: true,
    disableBackdropClick: false,
    placement: 'right' as DrawerPlacement,
}

type NativeAttrs = Omit<React.HTMLAttributes<any>, keyof Props>
export type DrawerProps = Props & NativeAttrs

const DrawerComponent: React.FC<React.PropsWithChildren<DrawerProps>> = ({
    visible: customVisible,
    keyboard,
    disableBackdropClick,
    onClose,
    onContentClick,
    wrapClassName,
    closeIcon,
    children,
    ...props
}: React.PropsWithChildren<DrawerProps> & typeof defaultProps) => {
    const portal = usePortal('drawer')
    const [visible, setVisible] = useState<boolean>(false)
    const [, setBodyHidden] = useBodyScroll(null, { delayReset: 300 })

    const closeDrawer = () => {
        onClose && onClose()
        setVisible(false)
        setBodyHidden(false)
    }

    useEffect(() => {
        if (typeof customVisible === 'undefined') return
        setVisible(customVisible)
        setBodyHidden(customVisible)
    }, [customVisible])

    const { bindings } = useKeyboard(
        () => {
            keyboard && closeDrawer()
        },
        KeyCode.Escape,
        {
            disableGlobalEvent: true,
        },
    )

    const closeFromBackdrop = () => {
        if (disableBackdropClick) return
        closeDrawer()
    }

    if (!portal) return null
    return createPortal(
        <Backdrop
            onClick={closeFromBackdrop}
            onContentClick={onContentClick}
            visible={visible}
            width="100%"
            {...bindings}>
            <DrawerWrapper visible={visible} className={wrapClassName} {...props}>
                {closeIcon && <button type='button' onClick={closeDrawer} className={clsx(s.closeIcon, s[props.placement])}><DrawerCloseIcon /></button>}
                {children}
            </DrawerWrapper>
        </Backdrop>,
        portal,
    )
}

DrawerComponent.defaultProps = defaultProps
DrawerComponent.displayName = 'SublimeDrawer'
const Drawer = DrawerComponent
export default Drawer